import errorMonitoring from 'errorMonitoring/ErrorMonitoring';
import autoSelectSeats from './autoSelectSeats';
import { describeSeatLayout, normalizeSeatNumber, parseSeatFloor } from './seatPreferenceMetadata';
import selectPreferredSeats, { INDIVIDUAL_MATCH } from './seatPreferenceSelection';
import { PASSENGER_COUNTS } from './seatPreferenceConstants';
import { readSeatPreferences } from './seatPreferencesStorage';

const coordinateKey = (id) => JSON.stringify(id);

const cloneValue = (value, seen = new WeakMap()) => {
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value)) return seen.get(value);

  const clone = Array.isArray(value) ? [] : {};
  seen.set(value, clone);
  Object.keys(value).forEach((key) => {
    clone[key] = cloneValue(value[key], seen);
  });
  return clone;
};

const freezeValue = (value, seen = new WeakSet()) => {
  if (!value || typeof value !== 'object' || seen.has(value)) return value;
  seen.add(value);
  Object.keys(value).forEach((key) => freezeValue(value[key], seen));
  return Object.freeze(value);
};

const reportSmartSelectionError = (error, passengerCount) => {
  try {
    errorMonitoring.notify({
      error,
      info: { from: 'quickPurchase:smartSeatSelection', passengerCount },
    });
  } catch (_monitoringError) {
    // Monitoring must not interfere with the original selector fallback.
  }
};

const numberIdentityFor = (number, descriptors) => {
  const candidates = descriptors.filter(({ preference }) => preference?.number === number);
  if (candidates.length === 0) return 'unknown';
  if (candidates.length > 1) return 'ambiguous';

  return candidates[0].reservationKey ? 'unique' : 'unknown';
};

const findDescriptor = (seat, match, descriptors) => {
  if (Array.isArray(match?.seatId)) {
    const key = coordinateKey(match.seatId);
    const exact = descriptors.find(({ id }) => coordinateKey(id) === key);
    if (exact) return exact;
  }

  const number = normalizeSeatNumber(seat?.number);
  if (number === null) return null;
  const parsedFloor = parseSeatFloor(seat?.seatLevel);
  const candidates = descriptors.filter(({ preference }) => preference?.number === number);
  const floorCandidates =
    parsedFloor.status === 'valid'
      ? candidates.filter(({ preference }) => preference.floor === parsedFloor.value)
      : candidates;
  const indexCandidates = floorCandidates.filter(
    ({ seat: candidate }) => Number.isFinite(seat?.index) && candidate.index === seat.index,
  );
  if (indexCandidates.length === 1) return indexCandidates[0];
  return floorCandidates.length === 1 ? floorCandidates[0] : null;
};

/**
 * Records what the complete canonical layout proves about one selected reservation identity.
 * Duplicate raw nodes already collapsed to one descriptor remain unique; unresolved physical
 * descriptors with the same number remain ambiguous even when they share a reservation key.
 * @param {object} params - Selected seat and canonical-layout context.
 * @returns {object} Immutable-result identity evidence used by downstream receipt validation.
 */
const identityEvidenceFor = ({ seat, descriptor, descriptors, legacyTransport }) => {
  const number = normalizeSeatNumber(seat?.number);
  const originalFloor = parseSeatFloor(seat?.seatLevel);
  const selectedFloor = descriptor?.preference?.floor || null;
  const knownIdentity = Boolean(descriptor?.reservationKey && number !== null);

  return {
    number,
    numberIdentity: number === null ? 'unknown' : numberIdentityFor(number, descriptors),
    selectedFloor,
    originalRequestFloor: originalFloor.status === 'valid' ? originalFloor.value : null,
    requestCanDisambiguateFloor: Boolean(
      !legacyTransport &&
        knownIdentity &&
        originalFloor.status === 'valid' &&
        selectedFloor === originalFloor.value,
    ),
  };
};

const unsupportedIdentity = (message) => {
  const error = new Error(message);
  error.code = 'unsupported-smart-seat-identity';
  return error;
};

const invalidResult = (message) => {
  const error = new Error(message);
  error.code = 'invalid-smart-seat-result';
  return error;
};

const positionsEqual = (left, right) => {
  if (left === null || right === null) return left === right;
  return (
    Array.isArray(left) &&
    Array.isArray(right) &&
    left.length === right.length &&
    left.every((position, index) => position === right[index])
  );
};

const seatBelongsToDescriptor = (seat, descriptor) => {
  const seatFloor = parseSeatFloor(seat?.seatLevel);
  const descriptorFloor = parseSeatFloor(descriptor?.seat?.seatLevel);
  return (
    normalizeSeatNumber(seat?.number) === descriptor?.preference?.number &&
    seatFloor.status === descriptorFloor.status &&
    seatFloor.value === descriptorFloor.value &&
    seat?.tripSlug === descriptor?.seat?.tripSlug
  );
};

/**
 * Validates the ranker's ordered seat-to-match association against the actual canonical layout.
 * Passenger indexes, labels, physical coordinates, preferences and reservation identities must
 * all agree; metadata from a different seat cannot make a malformed ranker result transportable.
 * @param {object} params - Ranked seats, matches and canonical descriptors.
 * @throws {Error} Invalid smart-result contract.
 */
const assertSelectionInvariants = ({ seats, matches, descriptors }) => {
  const validLabels = new Set(Object.values(INDIVIDUAL_MATCH));
  const usedCoordinates = new Set();
  const usedReservations = new Set();

  matches.forEach((match, passengerIndex) => {
    if (match?.passengerIndex !== passengerIndex || !validLabels.has(match?.match)) {
      throw invalidResult('Smart selection returned invalid ordered match metadata');
    }
    if (!Array.isArray(match.seatId)) {
      throw invalidResult('Smart selection returned a match without physical identity');
    }

    const coordinate = coordinateKey(match.seatId);
    const descriptor = descriptors.find(({ id }) => coordinateKey(id) === coordinate);
    if (!descriptor || !seatBelongsToDescriptor(seats[passengerIndex], descriptor)) {
      throw invalidResult('Smart selection seat and match identity disagree');
    }
    if (
      match.preference?.number !== descriptor.preference.number ||
      match.preference?.floor !== descriptor.preference.floor ||
      !positionsEqual(match.preference?.position ?? null, descriptor.preference.position)
    ) {
      throw invalidResult('Smart selection match preference disagrees with its descriptor');
    }
    if (
      usedCoordinates.has(coordinate) ||
      !descriptor.reservationKey ||
      usedReservations.has(descriptor.reservationKey)
    ) {
      throw invalidResult('Smart selection reused a reservation identity');
    }

    usedCoordinates.add(coordinate);
    usedReservations.add(descriptor.reservationKey);
  });
};

/**
 * Requires every valid ranked seat to be uniquely reservable by the active transport contract.
 * Full-layout ambiguity may be resolved only by an explicit floor the request can carry; ordinal
 * layout inference never strengthens number-only transport evidence.
 * @param {object} params - Ranked selection and transport context.
 * @throws {Error} Unsupported reservation identity.
 */
const assertTransportSupport = ({ seats, matches, descriptors, legacyTransport }) => {
  assertSelectionInvariants({ seats, matches, descriptors });
  seats.forEach((seat, index) => {
    const descriptor = findDescriptor(seat, matches[index], descriptors);
    if (!descriptor?.reservationKey) {
      throw unsupportedIdentity('Smart selection lost its reservation identity');
    }

    const evidence = identityEvidenceFor({ seat, descriptor, descriptors, legacyTransport });
    if (evidence.numberIdentity === 'unique') return;
    if (!evidence.requestCanDisambiguateFloor) {
      throw unsupportedIdentity('Smart selection uses an unsupported reservation identity');
    }
  });
};

const buildMatches = ({ seats, matches, descriptors, legacyTransport, algorithmFallback }) =>
  seats.map((seat, passengerIndex) => {
    const match = matches?.[passengerIndex] || {};
    const descriptor = findDescriptor(seat, match, descriptors);
    const preference = descriptor?.preference || match.preference || {};
    const identityEvidence = identityEvidenceFor({
      seat,
      descriptor,
      descriptors,
      legacyTransport,
    });
    const fallbackIdentityProven =
      identityEvidence.numberIdentity === 'unique' || identityEvidence.requestCanDisambiguateFloor;
    const exposeDescriptor = !algorithmFallback || fallbackIdentityProven;
    return {
      passengerIndex,
      seatId: descriptor && exposeDescriptor ? [...descriptor.id] : null,
      preference: {
        number: normalizeSeatNumber(preference.number ?? seat?.number),
        floor: descriptor && exposeDescriptor ? descriptor.preference.floor : null,
        position:
          descriptor?.preference.position && exposeDescriptor
            ? [...descriptor.preference.position]
            : null,
      },
      match: match.match || INDIVIDUAL_MATCH.FALLBACK,
      identityEvidence,
    };
  });

const buildSmartSelection = ({
  tripSlug,
  passengerCount,
  seats,
  matches,
  descriptors,
  legacyTransport,
  algorithmFallback,
}) =>
  freezeValue(
    cloneValue({
      tripSlug,
      passengerCount,
      seats,
      matches: buildMatches({
        seats,
        matches,
        descriptors,
        legacyTransport,
        algorithmFallback,
      }),
      algorithmFallback,
    }),
  );

const safelyDescribe = (layout, tripSlug, geometryOptions) => {
  try {
    return describeSeatLayout(layout, { ...geometryOptions, tripSlug });
  } catch (_error) {
    return [];
  }
};

/**
 * Selects automatic Quick Purchase seats behind the independent smart flag.
 * @param {object} params Selection context.
 * @returns {{seats: Array<object>, smartSelection: object|null}|null} Ordered selection.
 */
export const selectQuickPurchaseSeats = ({
  layout,
  tripSlug,
  passengerCount,
  smartSeatSelectionEnabled = false,
  supportedSmartScope = true,
  legacyTransport = false,
  diagramType,
  showUniformLayout = false,
}) => {
  const legacyFallback = () => autoSelectSeats(layout, tripSlug, passengerCount);
  if (
    smartSeatSelectionEnabled !== true ||
    !supportedSmartScope ||
    !PASSENGER_COUNTS.includes(passengerCount)
  ) {
    const seats = legacyFallback();
    return seats ? { seats, smartSelection: null } : null;
  }

  let preferences = {};
  try {
    preferences = readSeatPreferences();
  } catch (_storageError) {
    // Storage failure is ordinary no-history input.
  }

  const geometryOptions = { diagramType, showUniformLayout };
  try {
    const selection = selectPreferredSeats(
      layout,
      tripSlug,
      passengerCount,
      preferences,
      geometryOptions,
    );
    if (selection === null) return null;
    if (
      !Array.isArray(selection.seats) ||
      selection.seats.length !== passengerCount ||
      !Array.isArray(selection.matches) ||
      selection.matches.length !== passengerCount
    ) {
      throw new Error('Smart selector returned an incomplete selection');
    }

    const descriptors = describeSeatLayout(layout, { ...geometryOptions, tripSlug });
    assertTransportSupport({
      seats: selection.seats,
      matches: selection.matches,
      descriptors,
      legacyTransport,
    });
    return {
      seats: selection.seats,
      smartSelection: buildSmartSelection({
        tripSlug,
        passengerCount,
        seats: selection.seats,
        matches: selection.matches,
        descriptors,
        legacyTransport,
        algorithmFallback: false,
      }),
    };
  } catch (error) {
    reportSmartSelectionError(error, passengerCount);
    const seats = legacyFallback();
    if (!seats) return null;
    const descriptors = safelyDescribe(layout, tripSlug, geometryOptions);
    return {
      seats,
      smartSelection: buildSmartSelection({
        tripSlug,
        passengerCount,
        seats,
        matches: null,
        descriptors,
        legacyTransport,
        algorithmFallback: true,
      }),
    };
  }
};

export default selectQuickPurchaseSeats;
